import { FC, useState } from 'react'
import ConfirmModalContent from '@molecules/ConfirmModalContent'
import PrimaryButton from '@organisms/PrimaryButton'
import { ControlQuestion } from '@models/courseTheme'
import {
  AnswerStateIcon,
  ButtonWrapper,
  HintText,
  OptionInput,
  OptionRow,
  OptionShell,
  OptionText,
  OptionsWrapper,
  MessageText,
  QuestionCard,
  QuestionText,
  StyledTitle,
  Wrapper,
} from './styled'

type Props = {
  controlQuestion: ControlQuestion
  isCorrectlyAnswered: boolean
  wrongSelectedIndex?: number | null
  onAnswer: (selectedIndex: number) => Promise<void>
  onClose: () => void
  isSubmitting?: boolean
  onSelectionChange?: () => void
}

const ControlQuestionModal: FC<Props> = ({
  controlQuestion,
  isCorrectlyAnswered,
  wrongSelectedIndex = null,
  onAnswer,
  onClose,
  isSubmitting = false,
  onSelectionChange,
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const isError = !isCorrectlyAnswered && wrongSelectedIndex !== null

  const onSubmit = async () => {
    if (selectedIndex === null || isSubmitting) return

    await onAnswer(selectedIndex)
  }

  if (isCorrectlyAnswered) {
    return (
      <ConfirmModalContent
        leftIcon="success-bear-new.svg"
        title="Вы успешно прошли тему"
        rightContent={
          <ButtonWrapper>
            <PrimaryButton onClick={onClose} big>
              Закрыть
            </PrimaryButton>
          </ButtonWrapper>
        }
      />
    )
  }

  return (
    <Wrapper>
      <StyledTitle>Контрольный вопрос</StyledTitle>
      <HintText>Чтобы пройти тему, ответьте на контрольный вопрос</HintText>
      <QuestionCard>
        <QuestionText>{controlQuestion.question}</QuestionText>
        <OptionsWrapper>
          {controlQuestion.options.map((option, index) => {
            const isSelected = selectedIndex === index
            const isWrongSelected = isError && wrongSelectedIndex === index

            const optionInputStatus = isWrongSelected
              ? 'wrong'
              : isSelected
              ? 'selected'
              : 'default'

            return (
              <OptionShell key={index}>
                <AnswerStateIcon
                  $visible={isWrongSelected}
                  $isCorrect={false}
                  aria-hidden="true"
                >
                  ×
                </AnswerStateIcon>
                <OptionRow
                  $isWrongSelected={isWrongSelected}
                  $isSelected={isSelected}
                >
                  <OptionInput
                    type="radio"
                    checked={isSelected}
                    onChange={() => {
                      setSelectedIndex(index)
                      onSelectionChange?.()
                    }}
                    name="control-question-option"
                    disabled={isSubmitting}
                    $status={optionInputStatus}
                  />
                  <OptionText>{option.text}</OptionText>
                </OptionRow>
              </OptionShell>
            )
          })}
        </OptionsWrapper>
        {isError ? (
          <MessageText isError>Вы ответили неверно. Попробуйте ещё раз</MessageText>
        ) : null}
      </QuestionCard>
      <ButtonWrapper $compact>
        <PrimaryButton
          autoWidth
          disabled={selectedIndex === null || isSubmitting}
          onClick={onSubmit}
          withoutUpperCase
        >
          Ответить
        </PrimaryButton>
      </ButtonWrapper>
    </Wrapper>
  )
}

export default ControlQuestionModal
